import { Box, ContentLayout, Header, Select, Table, TableProps } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useApi } from '@/src/store/api.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FacilitatorAuditAction, FacilitatorAuditLogEntry } from '@/src/types/JamFacilitatorAuditLog';
import { JamMinimalParticipant } from '@/src/types/JamEvent';
import { AuditLogResolvers, AuditLogSentence, composeAuditLogSentence } from '@/src/utils/facilitator-action-log.utils';

const MISSING = '--';

interface ActionLogRow {
  key: string;
  time: string;
  timeValue: number;
  sentence: AuditLogSentence;
}

const FacilitatorActionLog = () => {
  const { t } = useTranslation();
  const { eventName } = useJamEventDetails();
  const { jamFacilitatorApi } = useApi();
  const { jamChallengeData } = useJamChallenge();

  const [entries, setEntries] = useState<FacilitatorAuditLogEntry[]>([]);
  const [participants, setParticipants] = useState<JamMinimalParticipant[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const allActionsOption = useMemo<OptionDefinition>(
    () => ({ label: t(i18nKeys.facilitator.actionLog.filter.all), value: '' }),
    [t]
  );
  const [actionFilter, setActionFilter] = useState<OptionDefinition>(allActionsOption);
  // Only the Time column is sortable; false = ascending (oldest first), the default.
  const [sortingDescending, setSortingDescending] = useState(false);
  // Track the event we've already attempted to load. useApi() returns a fresh jamFacilitatorApi on
  // every provider render (e.g. whenever a flashbar is added), so without this guard a failed load
  // would add an error flashbar, re-render the provider, re-fire this effect, and retry forever.
  const attemptedEventRef = useRef<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!eventName || attemptedEventRef.current === eventName) {
        return;
      }
      // Mark attempted up front so a failure does not retry; only a new event triggers a reload.
      attemptedEventRef.current = eventName;
      setIsFetching(true);
      try {
        // Audit log is the primary payload; participants are auxiliary (for name resolution), so
        // fetch them silently and don't let a participants failure block the log. The participants
        // endpoint is the source of truth for id -> name: each entry carries vibeId + login +
        // nickname, covering both the audit log's actorVibeId and its (login-or-vibeId) targetId.
        const [auditLog, participantsRes] = await Promise.all([
          jamFacilitatorApi.getFacilitatorAuditLog(eventName),
          jamFacilitatorApi.getEventParticipants(eventName, true).catch(() => ({ participants: [] })),
        ]);
        setEntries(auditLog ?? []);
        setParticipants(participantsRes?.participants ?? []);
      } catch {
        // Audit log failure is surfaced via the API fail flashbar.
      } finally {
        setIsFetching(false);
      }
    };
    void load();
  }, [eventName, jamFacilitatorApi]);

  // Map every id we can associate with a participant to their display name. The audit log's
  // actorVibeId is a vibe id, while targetId may be a vibe id or a legacy login, so we index by
  // both. Resolvers fall back to the raw id when a participant isn't in the loaded set.
  const idToName = useMemo(() => {
    const map = new Map<string, string>();
    participants.forEach((participant) => {
      const name = participant.nickname || participant.vibeId || participant.login;
      if (participant.vibeId) {
        map.set(participant.vibeId, name);
      }
      if (participant.login) {
        map.set(participant.login, name);
      }
    });
    return map;
  }, [participants]);

  const challengeTitles = useMemo(() => {
    const map = new Map<string, string>();
    (jamChallengeData?.challenges ?? []).forEach((challenge) => map.set(challenge.id, challenge.title));
    return map;
  }, [jamChallengeData]);

  const taskNumbers = useMemo(() => {
    const map = new Map<string, string>();
    (jamChallengeData?.challenges ?? []).forEach((challenge) => {
      (challenge.taskProgress ?? []).forEach((task) => map.set(task.taskId, String(task.taskNumber)));
    });
    return map;
  }, [jamChallengeData]);

  const resolvers = useMemo<AuditLogResolvers>(
    () => ({
      actorName: (id) => idToName.get(id) ?? id,
      userName: (id) => idToName.get(id) ?? id,
      challengeTitle: (id) => challengeTitles.get(id) ?? id,
      taskNumber: (id) => taskNumbers.get(id) ?? id,
    }),
    [idToName, challengeTitles, taskNumbers]
  );

  const actionFilterOptions = useMemo<OptionDefinition[]>(
    () => [
      allActionsOption,
      ...Object.values(FacilitatorAuditAction).map((action) => ({
        label: t(i18nKeys.facilitator.actionLog.actionTypes[action]),
        value: action,
      })),
    ],
    [allActionsOption, t]
  );

  const items = useMemo<ActionLogRow[]>(() => {
    const direction = sortingDescending ? -1 : 1;
    return (
      entries
        // Filter to the selected action type ('' = all actions).
        .filter((entry) => !actionFilter.value || entry.action === actionFilter.value)
        .map((entry, index) => ({
          key: `${entry.time}-${index}`,
          time: entry.time ? moment(entry.time).format('MM/DD/YYYY hh:mm A') : MISSING,
          timeValue: entry.time ? new Date(entry.time).getTime() : 0,
          sentence: composeAuditLogSentence(entry, resolvers),
        }))
        // A plain Table does not reorder rows itself, so we sort by time here. Ascending
        // (oldest first) by default; clicking the Time header flips the direction.
        .sort((a, b) => (a.timeValue - b.timeValue) * direction)
    );
  }, [entries, resolvers, actionFilter, sortingDescending]);

  const columnDefinitions = useMemo<TableProps.ColumnDefinition<ActionLogRow>[]>(
    () => [
      {
        id: 'time',
        header: t(i18nKeys.facilitator.actionLog.columns.time),
        cell: (item) => item.time,
        width: 220,
        sortingComparator: (a, b) => a.timeValue - b.timeValue,
      },
      {
        id: 'action',
        header: t(i18nKeys.facilitator.actionLog.columns.action),
        cell: (item) => (
          <Trans i18nKey={item.sentence.i18nKey} values={item.sentence.values} components={{ b: <b /> }} />
        ),
      },
    ],
    [t]
  );

  return (
    <ContentLayout header={<Header variant="h1">{t(i18nKeys.facilitator.actionLog.title)}</Header>}>
      <Table
        header={<Header description={t(i18nKeys.facilitator.actionLog.description)} />}
        filter={
          <div style={{ maxWidth: '300px' }}>
            <Select
              selectedOption={actionFilter}
              onChange={({ detail }) => setActionFilter(detail.selectedOption)}
              options={actionFilterOptions}
              ariaLabel={t(i18nKeys.facilitator.actionLog.filter.label)}
              data-testid="facilitator-action-log-filter"
            />
          </div>
        }
        items={items}
        trackBy="key"
        columnDefinitions={columnDefinitions}
        sortingColumn={columnDefinitions[0]}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => setSortingDescending(!!detail.isDescending)}
        empty={
          <Box textAlign="center">
            <b>
              {t(actionFilter.value ? i18nKeys.facilitator.actionLog.noMatch : i18nKeys.facilitator.actionLog.empty)}
            </b>
          </Box>
        }
        loading={isFetching}
        data-testid="facilitator-action-log-table"
      />
    </ContentLayout>
  );
};

export default FacilitatorActionLog;
